/**
 * SocialStudioView — daily platform-tailored social posts.
 * One button generates today's Facebook (caregivers), LinkedIn
 * (professionals) and X (investors) posts; copy, regenerate, mark posted.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Check, RefreshCcw, Sparkles, CheckCircle2, Loader2 } from "lucide-react";

const INVESTOR_FAQ = [
    {
        q: "Is it proven? What medical evidence backs this?",
        a: "The interventions are the proven part: caregiver skill-building (REACH II RCT — significant reductions in caregiver depression & burden) and care navigation (the entire basis of Medicare's GUIDE model). Memory Lane delivers them at software scale and runs the same validated instruments clinicians use (Zarit, FAST) natively — so every cohort builds the evidence base. Our own outcomes data starts with our pilot cohort.",
    },
    {
        q: "What traction do you have?",
        a: "850+ new users in the last 28 days, largely organic. 4.4/5 average feedback rating across 40 structured survey responses. Early PMF signal: 100% of respondents to the Sean Ellis question said they'd be 'very disappointed' to lose it (small n, benchmark is 40%). Retention loops working: win-back emails returned 7 lapsed caregivers in a single week.",
    },
    {
        q: "How big is the market?",
        a: "7M Americans with dementia today, 13M by 2050 — each with ~1.5 unpaid family caregivers making every care decision. Unpaid dementia caregiving is a $350B/yr shadow workforce. And for the first time, there's a payer: Medicare's GUIDE model reimburses dementia care navigation, respite, and caregiver support.",
    },
    {
        q: "What's the moat?",
        a: "Three compounding layers: (1) clinician-founded credibility — content written and reviewed by a dementia-care APRN, not scraped; (2) longitudinal data — we capture validated burden/staging instruments over time, which nobody collects at consumer scale; (3) workflow position — we sit between families and GUIDE providers with pre-visit packets both sides need.",
    },
    {
        q: "Why now?",
        a: "GUIDE launched in 2024 and scales through 2026+ — Medicare now pays for exactly what we orchestrate. The caregiver generation is digital-native (adult daughters, 45-60). And AI finally makes personalized, stage-aware guidance affordable to deliver at consumer prices.",
    },
];

function MetricTile({ label, value, sub, testid }) {
    return (
        <div className="border border-cream-dark rounded-2xl p-4 bg-white" data-testid={testid}>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6E7A74]">{label}</p>
            <p className="mt-1 text-2xl font-semibold text-[#1F2624] tabular-nums">{value}</p>
            {sub && <p className="mt-0.5 text-xs text-[#4C5551]">{sub}</p>}
        </div>
    );
}

const fmtPct = (p) => (p === null || p === undefined ? null : `${p >= 0 ? "+" : ""}${p}%`);

function InvestorMetrics() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);

    const load = async (refresh = false) => {
        setBusy(true);
        try {
            const r = await api.get(`/admin/investor-metrics${refresh ? "?refresh=1" : ""}`);
            setData(r.data);
        } catch {
            toast.error("Couldn't load investor metrics.");
        } finally {
            setBusy(false);
        }
    };
    useEffect(() => { load(); }, []);

    const copyBullets = async () => {
        try {
            await navigator.clipboard.writeText((data?.slide_bullets || []).map(b => `• ${b}`).join("\n"));
            setCopied(true);
            toast.success("Slide bullets copied — paste straight into your deck.");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Couldn't copy — select the text manually.");
        }
    };

    if (!data) {
        return (
            <div className="ml-card p-5 bg-white" data-testid="investor-metrics-card">
                <p className="text-sm text-[#6E7A74]">{busy ? "Computing investor metrics…" : "Investor metrics unavailable."}</p>
            </div>
        );
    }

    const s = data.signups || {};
    const u = data.usage || {};
    const ret = data.retention?.overall || {};
    const eng = data.engagement || {};
    const pmf = data.pmf;
    const study = data.study_participants;
    const ga4 = data.ga4_28d;
    const gsc = data.gsc_28d;
    const maxWeek = Math.max(1, ...(s.weekly_series || []).map(w => w.count));

    return (
        <div className="ml-card p-5 bg-white" data-testid="investor-metrics-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <p className="text-sm font-semibold text-[#1F2624]">Investor metrics — the numbers behind the slide</p>
                    <p className="text-xs text-[#6E7A74] mt-0.5">
                        Live from your database + GA4. Real caregivers only — admin, tester and internal accounts excluded. As of {new Date(data.as_of).toLocaleString()}.
                    </p>
                </div>
                <button type="button" onClick={() => load(true)} disabled={busy} data-testid="investor-metrics-refresh"
                    className="inline-flex items-center gap-1.5 rounded-2xl border border-cream-dark bg-white px-3.5 py-1.5 text-xs font-semibold text-[#4C5551] hover:border-teal-dark hover:text-teal-dark transition-colors disabled:opacity-60">
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCcw size={12} />} Recompute
                </button>
            </div>

            <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
                <MetricTile testid="investor-metric-signups" label="Real signups" value={s.total ?? "—"}
                    sub={fmtPct(s.mom?.pct_change) ? `${fmtPct(s.mom.pct_change)} MoM (${s.mom.previous} → ${s.mom.current})` : `${s.mom?.current ?? 0} in last 30 days`} />
                <MetricTile testid="investor-metric-wow" label="Signups this week" value={s.wow?.current ?? "—"}
                    sub={fmtPct(s.wow?.pct_change) ? `${fmtPct(s.wow.pct_change)} WoW (prev ${s.wow.previous})` : `prev week ${s.wow?.previous ?? 0}`} />
                <MetricTile testid="investor-metric-mau" label="DAU / WAU / MAU" value={`${u.dau ?? 0} / ${u.wau ?? 0} / ${u.mau ?? 0}`}
                    sub={u.stickiness_pct != null ? `${u.stickiness_pct}% DAU/MAU stickiness · ${u.source === "ga4" ? "GA4" : "product logins"}` : (u.source === "ga4" ? "GA4" : "product logins")} />
                <MetricTile testid="investor-metric-retention" label="Retention D1 / D7 / D30" value={`${ret.d1_pct ?? 0}% / ${ret.d7_pct ?? 0}% / ${ret.d30_pct ?? 0}%`}
                    sub={`last 12 weeks · n=${ret.size ?? 0}`} />
            </div>

            {pmf && (
                <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="investor-metrics-pmf-row">
                    <MetricTile testid="investor-metric-pmf" label="PMF — Sean Ellis test"
                        value={pmf.pmf?.very_pct != null ? `${pmf.pmf.very_pct}%` : "—"}
                        sub={pmf.pmf?.answered ? `'very disappointed' without it (${pmf.pmf.very_disappointed}/${pmf.pmf.answered} answered · benchmark 40%)` : "no PMF answers yet"} />
                    <MetricTile testid="investor-metric-rating" label="Avg product rating"
                        value={pmf.avg_rating?.avg != null ? `${pmf.avg_rating.avg}/5` : "—"}
                        sub={`${pmf.avg_rating?.n ?? 0} structured feedback responses`} />
                    <MetricTile testid="investor-metric-wom" label="Word of mouth"
                        value={pmf.avg_wom_likelihood?.avg != null ? `${pmf.avg_wom_likelihood.avg}/10` : "—"}
                        sub={pmf.avg_wom_likelihood?.n ? `'would recommend to another caregiver' (n=${pmf.avg_wom_likelihood.n})` : "not asked yet"} />
                    <MetricTile testid="investor-metric-study" label="Study participants"
                        value={study?.total ?? 0}
                        sub={study?.total ? `${study.prolific} Prolific · ${study.returned_2plus} returned 2+ times · ${study.completed_study} completed` : "structured research cohort"} />
                </div>
            )}

            {(ga4 || gsc) && (
                <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="investor-metrics-sources-row">
                    {ga4 && (
                        <div className="border border-cream-dark rounded-2xl p-4 bg-white" data-testid="investor-metric-ga4">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6E7A74]">Google Analytics — last 28 days</p>
                            <div className="mt-1.5 flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#4C5551]">
                                <span><strong className="text-[#1F2624] tabular-nums">{ga4.new_users}</strong> new users</span>
                                <span><strong className="text-[#1F2624] tabular-nums">{ga4.active_users}</strong> active users</span>
                                <span><strong className="text-[#1F2624] tabular-nums">{ga4.sessions}</strong> sessions</span>
                                <span><strong className="text-[#1F2624] tabular-nums">{ga4.conversions}</strong> conversion events</span>
                            </div>
                        </div>
                    )}
                    {gsc && (
                        <div className="border border-cream-dark rounded-2xl p-4 bg-white" data-testid="investor-metric-gsc">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6E7A74]">Google Search (organic) — last 28 days</p>
                            <div className="mt-1.5 flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#4C5551]">
                                <span><strong className="text-[#1F2624] tabular-nums">{(gsc.impressions ?? 0).toLocaleString()}</strong> impressions</span>
                                <span><strong className="text-[#1F2624] tabular-nums">{gsc.clicks}</strong> clicks</span>
                                <span><strong className="text-[#1F2624] tabular-nums">{gsc.ctr_pct}%</strong> CTR</span>
                                <span>avg position <strong className="text-[#1F2624] tabular-nums">{gsc.avg_position}</strong></span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {(s.weekly_series || []).length > 0 && (
                <div className="mt-4" data-testid="investor-metrics-weekly-chart">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6E7A74]">Signups by week (last 8)</p>
                    <div className="mt-2 flex items-end gap-2 h-20">
                        {s.weekly_series.map((w, i) => (
                            <div key={i} className="flex-1 flex flex-col items-center gap-1">
                                <span className="text-[10px] text-[#4C5551] tabular-nums">{w.count}</span>
                                <div className="w-full rounded-t bg-teal/80" style={{ height: `${Math.max(4, (w.count / maxWeek) * 56)}px` }} />
                                <span className="text-[9px] text-[#6E7A74]">{w.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1.5 text-xs text-[#4C5551]" data-testid="investor-metrics-engagement">
                <span><strong className="text-[#1F2624]">{eng.lane_conversations ?? 0}</strong> Lane conversations</span>
                <span><strong className="text-[#1F2624]">{eng.lane_user_messages ?? 0}</strong> caregiver questions asked</span>
                <span><strong className="text-[#1F2624]">{eng.guide_screeners_completed ?? 0}</strong> GUIDE screeners</span>
                <span><strong className="text-[#1F2624]">{eng.country_count ?? 0}</strong> countries{eng.countries?.length ? ` (${eng.countries.join(", ")})` : ""}</span>
            </div>

            {(data.slide_bullets || []).length > 0 && (
                <div className="mt-4 border border-cream-dark rounded-2xl p-4 bg-cream/40" data-testid="investor-metrics-bullets">
                    <div className="flex items-center justify-between gap-3">
                        <p className="text-xs font-semibold text-teal-dark uppercase tracking-wide">Copy-ready for your traction slide</p>
                        <button type="button" onClick={copyBullets} data-testid="investor-metrics-copy-bullets"
                            className="inline-flex items-center gap-1.5 rounded-2xl border border-cream-dark bg-white px-3.5 py-1.5 text-xs font-semibold text-[#4C5551] hover:border-teal-dark hover:text-teal-dark transition-colors">
                            {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy all"}
                        </button>
                    </div>
                    <ul className="mt-2 space-y-1.5">
                        {data.slide_bullets.map((b, i) => (
                            <li key={i} className="text-sm text-[#4C5551] leading-relaxed">• {b}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function InvestorFaq() {
    const [copiedIdx, setCopiedIdx] = useState(-1);
    const copy = async (text, i) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedIdx(i);
            toast.success("Copied — paste it as your reply.");
            setTimeout(() => setCopiedIdx(-1), 2000);
        } catch {
            toast.error("Couldn't copy — select the text manually.");
        }
    };
    return (
        <details className="ml-card p-5 bg-white" data-testid="investor-faq">
            <summary className="text-sm font-semibold text-[#1F2624] cursor-pointer">
                Investor FAQ — fast replies to the 5 most common diligence questions
            </summary>
            <div className="mt-4 space-y-3">
                {INVESTOR_FAQ.map((f, i) => (
                    <div key={i} className="border border-cream-dark rounded-2xl p-4" data-testid={`investor-faq-${i}`}>
                        <p className="text-xs font-semibold text-teal-dark uppercase tracking-wide">{f.q}</p>
                        <p className="mt-1.5 text-sm text-[#4C5551] leading-relaxed whitespace-pre-wrap">{f.a}</p>
                        <button type="button" onClick={() => copy(f.a, i)} data-testid={`investor-faq-copy-${i}`}
                            className="mt-2.5 inline-flex items-center gap-1.5 rounded-2xl border border-cream-dark bg-white px-3.5 py-1.5 text-xs font-semibold text-[#4C5551] hover:border-teal-dark hover:text-teal-dark transition-colors">
                            {copiedIdx === i ? <Check size={12} /> : <Copy size={12} />} {copiedIdx === i ? "Copied" : "Copy answer"}
                        </button>
                    </div>
                ))}
            </div>
        </details>
    );
}

const PLATFORM_META = {
    facebook: { label: "Facebook — caregivers", tone: "text-[#1877F2]", chip: "bg-[#1877F2]/10" },
    linkedin: { label: "LinkedIn — professionals", tone: "text-[#0A66C2]", chip: "bg-[#0A66C2]/10" },
    x: { label: "X — investors & founders", tone: "text-[#1F2624]", chip: "bg-cream-dark/60" },
};

function PostCard({ post, onRegenerate, onToggleStatus, busy, perf, onPublish, publishing }) {
    const [copied, setCopied] = useState(false);
    const meta = PLATFORM_META[post.platform] || { label: post.platform, tone: "text-[#1F2624]", chip: "bg-cream-dark/60" };
    const stats = perf?.by_content?.[`${post.date}-${post.platform}`];
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(post.content);
            setCopied(true);
            toast.success("Copied — paste it into " + post.platform);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Couldn't copy — select the text manually.");
        }
    };
    return (
        <div className="ml-card p-5 bg-white" data-testid={`social-post-${post.platform}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-2xl ${meta.chip} ${meta.tone}`}>{meta.label}</span>
                <span className="text-[11px] text-[#6E7A74]">{post.topic}</span>
            </div>
            {stats && (
                <p className="mt-2 text-[11px] font-semibold text-teal-dark" data-testid={`social-perf-${post.platform}`}>
                    {stats.sessions} click{stats.sessions !== 1 ? "s" : ""} · {stats.users} visitor{stats.users !== 1 ? "s" : ""}{stats.conversions ? ` · ${stats.conversions} conversions` : ""}
                </p>
            )}
            <p className="mt-3 text-sm text-[#1F2624] whitespace-pre-wrap leading-relaxed" data-testid={`social-post-content-${post.platform}`}>{post.content}</p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
                {onPublish && post.status !== "posted" && (
                    <button type="button" onClick={() => onPublish(post)} disabled={publishing}
                        data-testid={`social-publish-${post.platform}`}
                        className="inline-flex items-center gap-1.5 rounded-2xl bg-[#1F2624] text-white px-4 py-1.5 text-xs font-semibold hover:bg-black transition-colors disabled:opacity-50">
                        {publishing ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Post to X now
                    </button>
                )}
                <button type="button" onClick={copy} data-testid={`social-copy-${post.platform}`}
                    className="inline-flex items-center gap-1.5 rounded-2xl bg-teal text-white px-4 py-1.5 text-xs font-semibold hover:bg-teal-dark transition-colors">
                    {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy post"}
                </button>
                <button type="button" onClick={() => onRegenerate(post.platform)} disabled={busy}
                    data-testid={`social-regenerate-${post.platform}`}
                    className="inline-flex items-center gap-1.5 rounded-2xl border border-cream-dark bg-white px-4 py-1.5 text-xs font-semibold text-[#4C5551] hover:border-teal-dark hover:text-teal-dark transition-colors disabled:opacity-50">
                    <RefreshCcw size={13} className={busy ? "animate-spin" : ""} /> Regenerate
                </button>
                <button type="button" onClick={() => onToggleStatus(post)}
                    data-testid={`social-status-${post.platform}`}
                    className={`inline-flex items-center gap-1.5 rounded-2xl px-4 py-1.5 text-xs font-semibold border transition-colors ${post.status === "posted"
                        ? "bg-teal-soft/60 border-teal text-teal-dark"
                        : "bg-white border-cream-dark text-[#4C5551] hover:border-teal"}`}>
                    <CheckCircle2 size={13} /> {post.status === "posted" ? "Posted ✓" : "Mark posted"}
                </button>
            </div>
        </div>
    );
}

export default function SocialStudioView() {
    const [posts, setPosts] = useState([]);
    const [today, setToday] = useState("");
    const [generating, setGenerating] = useState(false);
    const [regenBusy, setRegenBusy] = useState("");
    const [perf, setPerf] = useState(null);
    const [settings, setSettings] = useState(null);
    const [publishing, setPublishing] = useState("");

    const load = async () => {
        try {
            const r = await api.get("/admin/social-posts");
            setPosts(r.data.posts || []);
            setToday(r.data.today || "");
        } catch {
            toast.error("Couldn't load social posts.");
        }
    };
    useEffect(() => { load(); }, []);
    useEffect(() => {
        api.get("/admin/social-posts/performance").then(r => setPerf(r.data)).catch(() => {});
        api.get("/admin/social-posts/settings").then(r => setSettings(r.data)).catch(() => {});
    }, []);

    const saveSettings = async (patch) => {
        const next = { ...settings, ...patch };
        setSettings(next);
        try {
            await api.put("/admin/social-posts/settings", { auto_post_enabled: next.auto_post_enabled, post_hour_utc: next.post_hour_utc });
            toast.success("Auto-post settings saved.");
        } catch {
            toast.error("Couldn't save settings.");
        }
    };

    const publishNow = async (post) => {
        setPublishing(post.id);
        try {
            const r = await api.post(`/admin/social-posts/${post.id}/publish`);
            toast.success("Published to X! Tweet ID " + (r.data.tweet_id || ""));
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Publish failed.");
        } finally {
            setPublishing("");
        }
    };

    const generate = async () => {
        setGenerating(true);
        try {
            await api.post("/admin/social-posts/generate", {});
            toast.success("Today's posts are ready.");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Generation failed — try again.");
        } finally {
            setGenerating(false);
        }
    };

    const regenerate = async (platform) => {
        setRegenBusy(platform);
        try {
            await api.post("/admin/social-posts/generate", { platform, force: true });
            toast.success(`Fresh ${platform} post generated.`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Regeneration failed.");
        } finally {
            setRegenBusy("");
        }
    };

    const toggleStatus = async (post) => {
        const next = post.status === "posted" ? "draft" : "posted";
        try {
            await api.put(`/admin/social-posts/${post.id}/status`, { status: next });
            setPosts(ps => ps.map(p => p.id === post.id ? { ...p, status: next } : p));
        } catch {
            toast.error("Couldn't update status.");
        }
    };

    const todays = posts.filter(p => p.date === today);
    const history = posts.filter(p => p.date !== today);

    return (
        <section className="space-y-6" data-testid="social-studio-view">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="text-2xl font-heading font-semibold text-[#12776F]">Social Studio</h2>
                    <p className="text-sm text-[#4C5551] mt-1 max-w-xl">
                        One fresh post per platform, every day — Facebook speaks to caregivers, LinkedIn to clinicians and
                        partners, X to investors. Topics rotate daily so a week never repeats. Every link is UTM-tagged.
                    </p>
                </div>
                <button type="button" onClick={generate} disabled={generating}
                    data-testid="social-generate-today"
                    className="inline-flex items-center gap-2 rounded-2xl bg-teal text-white px-5 py-2.5 text-sm font-semibold hover:bg-teal-dark transition-colors disabled:opacity-60">
                    {generating ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                    {generating ? "Writing today's posts…" : "Generate today's posts"}
                </button>
            </div>

            {settings && (
                <div className="ml-card p-5 bg-white" data-testid="social-autopost-card">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                            <p className="text-sm font-semibold text-[#1F2624]">
                                Auto-post to X {settings.x_connected
                                    ? <span className="text-teal-dark">· connected ✓</span>
                                    : <span className="text-coral-dark">· not connected</span>}
                            </p>
                            <p className="text-xs text-[#6E7A74] mt-0.5">
                                {settings.x_connected
                                    ? "When enabled, the day's X post is written and published automatically at your chosen hour."
                                    : "Waiting for X API credentials — auto-posting activates once they're added."}
                            </p>
                        </div>
                        {settings.x_connected && (
                            <div className="flex items-center gap-3">
                                <select
                                    value={settings.post_hour_utc}
                                    onChange={e => saveSettings({ post_hour_utc: parseInt(e.target.value, 10) })}
                                    data-testid="social-autopost-hour"
                                    className="rounded-2xl border border-cream-dark bg-white px-3 py-1.5 text-xs"
                                >
                                    {Array.from({ length: 24 }, (_, h) => (
                                        <option key={h} value={h}>{`${h}:00 UTC (${(h - 4 + 24) % 24}:00 ET)`}</option>
                                    ))}
                                </select>
                                <button type="button" onClick={() => saveSettings({ auto_post_enabled: !settings.auto_post_enabled })}
                                    data-testid="social-autopost-toggle"
                                    className={`rounded-2xl px-4 py-1.5 text-xs font-semibold border transition-colors ${settings.auto_post_enabled
                                        ? "bg-teal text-white border-teal" : "bg-white text-[#4C5551] border-cream-dark hover:border-teal"}`}>
                                    {settings.auto_post_enabled ? "Auto-post ON" : "Auto-post OFF"}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {todays.length > 0 ? (
                <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-4" data-testid="social-today-grid">
                    {["facebook", "linkedin", "x"].map(pf => {
                        const p = todays.find(t => t.platform === pf);
                        return p ? <PostCard key={p.id} post={p} onRegenerate={regenerate} onToggleStatus={toggleStatus} busy={regenBusy === pf} perf={perf} /> : null;
                    })}
                </div>
            ) : (
                <div className="ml-card p-8 text-center bg-white" data-testid="social-empty-state">
                    <p className="text-sm text-[#4C5551]">No posts for today yet — hit “Generate today's posts” and Lane will write all three.</p>
                </div>
            )}

            {perf?.by_source && Object.keys(perf.by_source).length > 0 && (
                <div className="ml-card p-5 bg-white" data-testid="social-perf-summary">
                    <p className="text-sm font-semibold text-[#1F2624]">Last {perf.days} days — traffic from your daily posts (GA4)</p>
                    <div className="mt-3 flex flex-wrap gap-x-8 gap-y-2">
                        {Object.entries(perf.by_source).map(([src, s]) => (
                            <div key={src} className="flex items-baseline gap-1.5">
                                <span className="text-lg font-semibold text-teal-dark tabular-nums">{s.sessions}</span>
                                <span className="text-xs text-[#4C5551]">clicks from {src}{s.conversions ? ` (${s.conversions} conv)` : ""}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <InvestorMetrics />

            <InvestorFaq />

            {history.length > 0 && (
                <details className="ml-card p-5 bg-white" data-testid="social-history">
                    <summary className="text-sm font-semibold text-[#4C5551] cursor-pointer">Previous days ({history.length} posts)</summary>
                    <div className="mt-4 space-y-3">
                        {history.map(p => {
                            const s = perf?.by_content?.[`${p.date}-${p.platform}`];
                            return (
                                <div key={p.id} className="border border-cream-dark rounded-2xl p-4">
                                    <div className="flex items-center gap-2 text-[11px] text-[#6E7A74]">
                                        <span className="font-semibold uppercase">{p.platform}</span> · {p.date} · {p.topic} · {p.status}
                                        {s && <span className="font-semibold text-teal-dark">· {s.sessions} clicks</span>}
                                    </div>
                                    <p className="mt-2 text-xs text-[#4C5551] whitespace-pre-wrap">{p.content}</p>
                                </div>
                            );
                        })}
                    </div>
                </details>
            )}
        </section>
    );
}
